// @flow
import CategoryFooter from './CategoryFooter';
import CategoryLandingPageBreadCrumbs from './CategoryLandingPageBreadCrumbs';
import CategoryLandingPageHeader from './CategoryLandingPageHeader';
import CategoryLandingPageHeaderMeta from './CategoryLandingPageHeaderMeta';
import DeletedItemMessage from './DeletedItemMessage';
import isEqual from 'react-fast-compare';
import React from 'react';
import SearchError from '../../components/SearchError/SearchError';
import SearchFacets from '../../components/SearchFacets/SearchFacets';
import SearchHeader from './SearchHeader';
import SearchPageHeaderMeta from './SearchPageHeaderMeta';
import SearchResults from '../../components/SearchResults/SearchResults';
import styles from './SearchPage.module.scss';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';

type Props = $ReadOnly<{|
    archivedSearch: boolean,
    baseUrl: string,
    category?: any,
    changePageAndNavigate: Function,
    changePageSizeAndNavigate: Function,
    changeSearchStatus?: Function,
    clearAllParamsExceptCategories: Function,
    deletedItemTitle?: string,
    deleteSavedSearch: Function,
    distanceSelected: Function,
    excludedFacets?: any,
    facetOptionChecked: Function,
    facetOptionExcluded: Function,
    facets: any[],
    houses?: any[],
    isLandingPage?: boolean,
    isLoading: boolean,
    items: any[],
    landingPageMetaData?: any,
    liveItemsFound?: number,
    page: number,
    pageLabel?: string,
    pageName: string,
    pageSize: number,
    pathname: string,
    promotedItems: any[],
    queryParams: any,
    rangeEntered: Function,
    removeCountryAndDistance: Function,
    removeFacetById: Function,
    removePublishedDate: Function,
    replaceCountry: Function,
    saveSearchCheckState: any,
    saveSearchState: any,
    searchFailed: boolean,
    searchQuery: any,
    searchSubmitted: boolean,
    selectedCreator?: any,
    showLandingPageLinks?: boolean,
    soldItemsFound?: number,
    sortOptions: any[],
    sortSelected: Function,
    submitSaveSearch: Function,
    taxonomyAllSelected: Function,
    taxonomyParentSelected: Function,
    taxonomySelected: Function,
    totalItemsFound: number,
    totalPages: number,
    trackItemClick?: Function,
    whiteLabel?: boolean,
    width: number,
|}>;

const SearchPage = (props: Props) => {
    const {
        archivedSearch,
        baseUrl,
        category,
        changePageAndNavigate,
        changePageSizeAndNavigate,
        changeSearchStatus,
        clearAllParamsExceptCategories,
        deletedItemTitle,
        deleteSavedSearch,
        distanceSelected,
        excludedFacets,
        facetOptionChecked,
        facetOptionExcluded,
        facets,
        houses,
        isLandingPage = false,
        isLoading,
        items,
        landingPageMetaData,
        liveItemsFound,
        page,
        pageLabel = '',
        pageName,
        pageSize,
        pathname,
        promotedItems,
        queryParams,
        rangeEntered,
        removeCountryAndDistance,
        removeFacetById,
        removePublishedDate,
        replaceCountry,
        saveSearchCheckState,
        saveSearchState,
        searchFailed,
        searchQuery,
        searchSubmitted,
        selectedCreator,
        showLandingPageLinks = true,
        soldItemsFound,
        sortOptions,
        sortSelected,
        submitSaveSearch,
        taxonomyAllSelected,
        taxonomyParentSelected,
        taxonomySelected,
        totalItemsFound,
        totalPages,
        trackItemClick,
        whiteLabel = false,
        width,
    } = props;

    const { campaignId, distance, publishDate, ranges, searchTerm, sort } = searchQuery;
    const firstHouse = houses?.[0];
    const showNoResults = totalItemsFound === 0 && !searchFailed && !isLoading;
    const isBaseCategoryPage = pathname === '/c/';
    let canonicalURL = landingPageMetaData ? landingPageMetaData.canonicalURL : pathname ? pathname : '';
    canonicalURL = canonicalURL || pathname || '';
    if (pathname.indexOf('price-guide') !== -1) {
        canonicalURL = canonicalURL.replace('/c/', '/price-guide/');
    }

    const searchPageHeaderMeta = isLandingPage ? (
        <CategoryLandingPageHeaderMeta
            {...landingPageMetaData}
            archivedSearch={archivedSearch}
            baseUrl={baseUrl}
            canonicalURL={canonicalURL}
            category={category}
            firstItem={items.length > 0 ? items[0] : null}
            pageLabel={pageLabel}
            pathname={pathname}
            queryParams={queryParams}
        />
    ) : (
        <SearchPageHeaderMeta
            baseUrl={baseUrl}
            searchTerm={searchTerm}
            firstItem={items.length > 0 ? items[0] : null}
        />
    );
    const searchPageHeader = isLandingPage ? (
        !isBaseCategoryPage && (
            <CategoryLandingPageBreadCrumbs
                clearAllParamsExceptCategories={clearAllParamsExceptCategories}
                distance={distance}
                facetOptionChecked={facetOptionChecked}
                facets={facets}
                publishDate={publishDate}
                ranges={ranges}
                removeCountryAndDistance={removeCountryAndDistance}
                removeFacetById={removeFacetById}
                removePublishedDate={removePublishedDate}
                taxonomyAllSelected={taxonomyAllSelected}
                taxonomyParentSelected={taxonomyParentSelected}
                whiteLabel={whiteLabel}
                zeroResults={!totalItemsFound}
            />
        )
    ) : (
        <SearchHeader
            archivedSearch={archivedSearch}
            changeSearchStatus={changeSearchStatus}
            clearAllParamsExceptCategories={clearAllParamsExceptCategories}
            deleteSavedSearch={deleteSavedSearch}
            distance={distance}
            facetOptionChecked={facetOptionChecked}
            facets={facets}
            firstHouse={firstHouse}
            liveItemsFound={liveItemsFound}
            page={page}
            pageSize={pageSize}
            publishDate={publishDate}
            ranges={ranges}
            removeCountryAndDistance={removeCountryAndDistance}
            removeFacetById={removeFacetById}
            removePublishedDate={removePublishedDate}
            replaceCountry={replaceCountry}
            saveSearchCheckState={saveSearchCheckState}
            saveSearchState={saveSearchState}
            searchSubmitted={searchSubmitted}
            searchTerm={searchTerm}
            showNoResults={showNoResults}
            soldItemsFound={soldItemsFound}
            sort={sort}
            sortOptions={sortOptions}
            sortSelected={sortSelected}
            submitSaveSearch={submitSaveSearch}
            taxonomyAllSelected={taxonomyAllSelected}
            taxonomyParentSelected={taxonomyParentSelected}
            totalItemsFound={totalItemsFound}
            whiteLabel={whiteLabel}
            distanceSelected={distanceSelected}
            excludedFacets={excludedFacets}
            facetOptionExcluded={facetOptionExcluded}
            isLandingPage={isLandingPage}
            pageName={pageName}
            pathname={pathname}
            queryParams={queryParams}
            rangeEntered={rangeEntered}
            showLandingPageLinks={showLandingPageLinks}
            taxonomySelected={taxonomySelected}
            width={width}
        />
    );

    const categoryFooter = isLandingPage && (
        <CategoryFooter
            archivedSearch={archivedSearch}
            campaignId={campaignId}
            category={category}
            deleteSavedSearch={deleteSavedSearch}
            isLoading={isLoading}
            pageLabel={pageLabel}
            saveSearchCheckState={saveSearchCheckState}
            saveSearchState={saveSearchState}
            submitSaveSearch={submitSaveSearch}
            totalItemsFound={totalItemsFound}
        />
    );

    return (
        <div className={styles['search-page']}>
            {searchPageHeaderMeta}
            {isLandingPage && deletedItemTitle && <DeletedItemMessage itemTitle={deletedItemTitle} />}
            {!searchFailed && !isLoading && searchPageHeader}
            {!searchFailed && (
                <div className={styles['search-page-items']}>
                    <SearchFacets
                        clearAllParamsExceptCategories={clearAllParamsExceptCategories}
                        desktop
                        distance={distance}
                        distanceSelected={distanceSelected}
                        excludedFacets={excludedFacets}
                        facetOptionChecked={facetOptionChecked}
                        facetOptionExcluded={facetOptionExcluded}
                        facets={facets}
                        isLandingPage={isLandingPage}
                        pageName={pageName}
                        pathname={pathname}
                        queryParams={queryParams}
                        rangeEntered={rangeEntered}
                        ranges={ranges}
                        removeCountryAndDistance={removeCountryAndDistance}
                        replaceCountry={replaceCountry}
                        showLandingPageLinks={showLandingPageLinks}
                        taxonomyAllSelected={taxonomyAllSelected}
                        taxonomyParentSelected={taxonomyParentSelected}
                        taxonomySelected={taxonomySelected}
                        totalItemsFound={totalItemsFound}
                        whiteLabel={whiteLabel}
                        width={width}
                    />
                    <div className={styles['search-page-results-wrapper']}>
                        {isLandingPage && (
                            <CategoryLandingPageHeader
                                archivedSearch={archivedSearch}
                                campaignId={campaignId}
                                clearAllParamsExceptCategories={clearAllParamsExceptCategories}
                                deleteSavedSearch={deleteSavedSearch}
                                distance={distance}
                                distanceSelected={distanceSelected}
                                excludedFacets={excludedFacets}
                                facetOptionChecked={facetOptionChecked}
                                facetOptionExcluded={facetOptionExcluded}
                                facets={facets}
                                isLandingPage={isLandingPage}
                                isLoading={isLoading}
                                pageLabel={pageLabel}
                                pageName={pageName}
                                pathname={pathname}
                                queryParams={queryParams}
                                rangeEntered={rangeEntered}
                                ranges={ranges}
                                removeCountryAndDistance={removeCountryAndDistance}
                                replaceCountry={replaceCountry}
                                saveSearchCheckState={saveSearchCheckState}
                                saveSearchState={saveSearchState}
                                selectedCreator={selectedCreator}
                                showLandingPageLinks={showLandingPageLinks}
                                sort={sort}
                                sortOptions={sortOptions}
                                sortSelected={sortSelected}
                                submitSaveSearch={submitSaveSearch}
                                taxonomyAllSelected={taxonomyAllSelected}
                                taxonomyParentSelected={taxonomyParentSelected}
                                taxonomySelected={taxonomySelected}
                                totalItemsFound={totalItemsFound}
                                whiteLabel={whiteLabel}
                                width={width}
                            />
                        )}
                        {totalItemsFound > 0 && (
                            <SearchResults
                                baseUrl={baseUrl}
                                canonicalURL={canonicalURL}
                                changePageAndNavigate={changePageAndNavigate}
                                changePageSizeAndNavigate={changePageSizeAndNavigate}
                                items={items}
                                page={page}
                                pageSize={pageSize}
                                pathname={pathname}
                                promotedItems={promotedItems}
                                trackItemClick={trackItemClick}
                                totalItemsFound={totalItemsFound}
                                totalPages={totalPages}
                                width={width}
                            />
                        )}
                    </div>
                </div>
            )}
            {categoryFooter}
            {searchFailed && <SearchError />}
            {searchSubmitted && <Throbber large className={styles.loading} />}
        </div>
    );
};

export default React.memo<Props>(SearchPage, isEqual);
